/**
 * ApiRequestsSlowestItems - Componente KPI Top 20 Requisições Mais Lentas
 * 
 * Exibe as 20 requisições API com maior tempo de resposta nas últimas 24 horas.
 * Permite expansão de cada linha para visualizar request/response payloads.
 * 
 * Estrutura:
 * - Tabela compacta com medalhas para top 3
 * - Collapsible com tabs para Request e Response
 * - Destaque visual para itens muito lentos (> 5s)
 */

import { useState, Fragment } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Clock, 
  ChevronDown, 
  ChevronRight, 
  AlertTriangle, 
  ArrowDownLeft, 
  ArrowUpRight,
  CheckCircle2,
  XCircle,
  Timer
} from 'lucide-react';
import { useSlowestApiRequests, type SlowestApiRequest } from '@/hooks/useObservability';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '@/lib/utils';

// ============= Componentes Internos =============

/**
 * Linha individual da tabela com Collapsible para payloads
 */
function SlowestRequestRow({ 
  item, 
  index 
}: { 
  item: SlowestApiRequest; 
  index: number;
}) {
  const [isOpen, setIsOpen] = useState(false);

  // Medalhas para top 3
  const getMedalColor = (pos: number) => {
    if (pos === 0) return 'text-yellow-500'; // Ouro
    if (pos === 1) return 'text-gray-400';   // Prata
    if (pos === 2) return 'text-amber-600';  // Bronze
    return 'text-muted-foreground';
  };

  // Status badge
  const renderStatusBadge = (status: string) => {
    switch (status) {
      case 'success':
        return (
          <Badge variant="outline" className="text-[10px] px-1 py-0 bg-green-500/10 text-green-600 border-green-500/20">
            <CheckCircle2 className="h-2.5 w-2.5 mr-0.5" />OK
          </Badge>
        );
      case 'error':
        return (
          <Badge variant="outline" className="text-[10px] px-1 py-0 bg-red-500/10 text-red-600 border-red-500/20">
            <XCircle className="h-2.5 w-2.5 mr-0.5" />Erro
          </Badge>
        );
      default:
        return (
          <Badge variant="outline" className="text-[10px] px-1 py-0">
            {status}
          </Badge>
        );
    }
  };

  // Direction badge
  const renderDirectionBadge = (direction: string) => {
    if (direction === 'inbound') {
      return (
        <Badge variant="outline" className="text-[10px] px-1 py-0 bg-cyan-500/10 text-cyan-600">
          <ArrowDownLeft className="h-2.5 w-2.5" />
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="text-[10px] px-1 py-0 bg-indigo-500/10 text-indigo-600">
        <ArrowUpRight className="h-2.5 w-2.5" />
      </Badge>
    );
  };

  // Destaque para itens muito lentos (> 5s)
  const isVerySlow = item.response_time_ms > 5000;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} asChild>
      <Fragment>
        <TableRow 
          className={cn(
            "cursor-pointer hover:bg-muted/50 transition-colors",
            isVerySlow && "bg-red-500/5"
          )}
        >
        {/* # - Posição com medalha */}
        <TableCell className="py-1.5 w-[40px]">
          <span className={cn("text-xs font-medium", getMedalColor(index))}>
            {index < 3 ? '🏅' : `${index + 1}º`}
          </span>
        </TableCell>

        {/* Tempo de Resposta */}
        <TableCell className="py-1.5 w-[70px]">
          <div className="flex items-center gap-1">
            <Clock className={cn("h-3 w-3", isVerySlow ? "text-red-500" : "text-muted-foreground")} />
            <span className={cn("text-xs font-mono", isVerySlow && "text-red-600 font-semibold")}>
              {item.response_time_formatted}
            </span>
          </div>
        </TableCell>

        {/* Provider */}
        <TableCell className="py-1.5 w-[80px]">
          <Badge variant="outline" className="text-[10px] px-1 py-0">
            {item.provider_slug}
          </Badge>
        </TableCell>

        {/* Operação */}
        <TableCell className="py-1.5 w-[90px]">
          <span className="text-xs truncate block max-w-[80px]" title={item.operation_type}>
            {item.operation_type}
          </span>
        </TableCell>

        {/* Direção */}
        <TableCell className="py-1.5 w-[45px]">
          {renderDirectionBadge(item.direction)}
        </TableCell>

        {/* Origem */}
        <TableCell className="py-1.5 w-[70px]">
          <span className="text-xs">{item.source_type}</span>
        </TableCell>

        {/* Status */}
        <TableCell className="py-1.5 w-[60px]">
          {renderStatusBadge(item.status)}
        </TableCell>

        {/* HTTP */}
        <TableCell className="py-1.5 w-[45px]">
          <span className={cn(
            "text-xs font-mono",
            item.response_status && item.response_status >= 400 ? "text-red-600" : "text-green-600"
          )}>
            {item.response_status || '-'}
          </span>
        </TableCell>

        {/* Tenant */}
        <TableCell className="py-1.5 max-w-[100px]">
          <span className="text-xs truncate block" title={item.tenant_name}>
            {item.tenant_name || 'Sistema'}
          </span>
        </TableCell>

        {/* Criado */}
        <TableCell className="py-1.5 w-[80px]">
          <span className="text-xs text-muted-foreground" title={item.created_at}>
            {formatDistanceToNow(new Date(item.created_at), { addSuffix: true, locale: ptBR })}
          </span>
        </TableCell>

        {/* Botão Expandir */}
        <TableCell className="py-1.5 w-[35px]">
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="icon" className="h-6 w-6">
              {isOpen ? (
                <ChevronDown className="h-3 w-3" />
              ) : (
                <ChevronRight className="h-3 w-3" />
              )}
            </Button>
          </CollapsibleTrigger>
        </TableCell>
      </TableRow>

      {/* Conteúdo Expandido - Request/Response Payloads */}
      <CollapsibleContent asChild>
        <TableRow className="bg-muted/30">
          <TableCell colSpan={11} className="py-2">
            <div className="pl-4 pr-2">
              <Tabs defaultValue="request" className="w-full">
                <TabsList className="h-7 mb-2">
                  <TabsTrigger value="request" className="text-xs h-6 px-2">
                    Request Payload
                  </TabsTrigger>
                  <TabsTrigger value="response" className="text-xs h-6 px-2">
                    Response Payload
                  </TabsTrigger>
                  {item.error_message && (
                    <TabsTrigger value="error" className="text-xs h-6 px-2 text-red-600">
                      Erro
                    </TabsTrigger>
                  )}
                </TabsList>

                <TabsContent value="request" className="mt-0">
                  <ScrollArea className="h-[150px] rounded border bg-background p-2">
                    <pre className="text-[10px] font-mono whitespace-pre-wrap">
                      {item.request_payload 
                        ? JSON.stringify(item.request_payload, null, 2) 
                        : 'Sem payload de request'}
                    </pre>
                  </ScrollArea>
                </TabsContent>

                <TabsContent value="response" className="mt-0">
                  <ScrollArea className="h-[150px] rounded border bg-background p-2">
                    <pre className="text-[10px] font-mono whitespace-pre-wrap">
                      {item.response_payload 
                        ? JSON.stringify(item.response_payload, null, 2) 
                        : 'Sem payload de response'}
                    </pre>
                  </ScrollArea>
                </TabsContent>

                {item.error_message && (
                  <TabsContent value="error" className="mt-0">
                    <div className="p-2 rounded border bg-red-500/10 text-red-600">
                      <p className="text-xs font-mono">{item.error_message}</p>
                    </div>
                  </TabsContent>
                )}
              </Tabs>

              {/* Endpoint completo */}
              {item.endpoint && (
                <div className="mt-2 text-[10px] text-muted-foreground">
                  <span className="font-medium">Endpoint:</span> {item.endpoint}
                </div>
              )}
            </div>
          </TableCell>
        </TableRow>
        </CollapsibleContent>
      </Fragment>
    </Collapsible>
  );
}

/**
 * Skeleton de carregamento
 */
function LoadingSkeleton() {
  return (
    <div className="space-y-2">
      {[...Array(5)].map((_, i) => (
        <div key={i} className="flex gap-2 items-center">
          <Skeleton className="h-8 w-full" />
        </div>
      ))}
    </div>
  );
}

/**
 * Estado vazio
 */
function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
      <Timer className="h-10 w-10 mb-2 opacity-50" />
      <p className="text-sm">Nenhuma requisição nas últimas 24 horas</p>
    </div>
  );
}

// ============= Componente Principal =============

export function ApiRequestsSlowestItems() {
  const { data: items, isLoading, error } = useSlowestApiRequests(20);

  if (error) {
    return (
      <Card>
        <CardContent className="py-6">
          <div className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-4 w-4" />
            <span className="text-sm">Erro ao carregar dados</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Clock className="h-5 w-5 text-orange-500" />
          <CardTitle className="text-base">Top 20 - Maior Tempo de Execução</CardTitle>
        </div>
        <CardDescription className="text-xs">
          Requisições API com maior latência nas últimas 24 horas
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <LoadingSkeleton />
        ) : !items?.length ? (
          <EmptyState />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs py-2 w-[40px]">#</TableHead>
                  <TableHead className="text-xs py-2 w-[70px]">Tempo</TableHead>
                  <TableHead className="text-xs py-2 w-[80px]">Provider</TableHead>
                  <TableHead className="text-xs py-2 w-[90px]">Operação</TableHead>
                  <TableHead className="text-xs py-2 w-[45px]">Dir</TableHead>
                  <TableHead className="text-xs py-2 w-[70px]">Origem</TableHead>
                  <TableHead className="text-xs py-2 w-[60px]">Status</TableHead>
                  <TableHead className="text-xs py-2 w-[45px]">HTTP</TableHead>
                  <TableHead className="text-xs py-2 max-w-[100px]">Tenant</TableHead>
                  <TableHead className="text-xs py-2 w-[80px]">Criado</TableHead>
                  <TableHead className="text-xs py-2 w-[35px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item, index) => (
                  <SlowestRequestRow key={item.id} item={item} index={index} />
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
